"use client";

import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { useEffect, useRef, useState } from "react";

import { CoverSearchItem } from "@/components/fileCoverInput/inputMethods/coverUpload/selectCoverModal/CoverSearchItem";
import { ImportantText } from "@/components/ImportantText";
import { Modal } from "@/components/Modal";
import { SoftText } from "@/components/text/SoftText";
import { coverSearchAction } from "@/data/cover/actions/coverSearchAction";
import { AudioInputState, Cover, UserNoDates } from "@/types/types";
import { convertToNormalText } from "@/utils/convertToNormalText";

export function SelectCoverModal({
  open,
  closeModal,
  adjustAcoustic,
  showSelectCoverText,
  user,
  checkSubscription,
  setInvalidLengthSubscriptionModalOpen,
  audioLengthMinLimitMinutes,
  audioLengthMaxLimitMinutes,
  setInvalidLengthMaxModalOpen,
  showCover,
  setInputCover,
}: {
  open: boolean;
  closeModal: () => void;
  adjustAcoustic: boolean | null;
  showSelectCoverText: boolean | null;
  user: UserNoDates | null;
  checkSubscription: boolean;
  setInvalidLengthSubscriptionModalOpen: (value: boolean) => void;
  audioLengthMinLimitMinutes: number | null;
  audioLengthMaxLimitMinutes: number;
  setInvalidLengthMaxModalOpen: (value: boolean) => void;
  showCover: boolean;
  setInputCover: AudioInputState["setInputCover"];
}) {
  const [searchString, setSearchString] = useState("");
  const [searchResults, setSearchResults] = useState<Cover[]>([]);
  const [openCoverId, setOpenCoverId] = useState<Cover["id"] | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const search = async () => {
    const searchResultsTemp = await coverSearchAction({
      query: searchString,
      adjustAcoustic: adjustAcoustic,
    });
    const searchResultsTempCleaned = searchResultsTemp.map((cover) => ({
      ...cover,
      title: convertToNormalText(cover.title),
    }));

    setSearchResults(searchResultsTempCleaned);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchString.length > 0) {
        search(); // Call your search function here
      } else {
        setSearchResults([]);
      }
    }, 400); // Delay in milliseconds

    return () => clearTimeout(delayDebounceFn);
  }, [searchString]);

  useEffect(() => {
    // Reset search when modal opens
    if (open) {
      setSearchString("");
      setSearchResults([]);
      setOpenCoverId(null);
    }
  }, [open]);

  useEffect(() => {
    // Handle focus on input when modal opens
    if (open) {
      // Delay focus a little to ensure the input is rendered
      const timer = setTimeout(() => {
        if (inputRef.current !== null) {
          inputRef.current.focus();
        }
      }, 100); // 100ms delay

      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    // Reset open cover when search results change
    if (openCoverId !== null) {
      setOpenCoverId(null);
    }
  }, [searchResults]);

  return (
    <Modal
      open={open}
      closeModal={closeModal}
      title="Select song"
      widthMode="wide"
    >
      {showSelectCoverText !== null && showSelectCoverText && (
        <ImportantText className="mb-6">
          Try to find a version of the track without background vocals, heavy
          instrumentals, and effects such as reverb and delay. Otherwise, use
          the isolation settings. Acoustic versions of songs are often a good
          choice.
        </ImportantText>
      )}

      <div className="mb-4 w-full">
        <label htmlFor="search" className="sr-only">
          Search
        </label>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            name="search"
            id="search"
            className="block w-full rounded-full border-0 py-4 pl-12 pr-8 text-gray-900 shadow-sm ring-1 ring-inset ring-primary placeholder:text-gray-400"
            placeholder="What do you want to cover?"
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
          />
          <div className="pointer-events-none absolute bottom-0 left-0 top-0 flex items-center pl-4">
            <MagnifyingGlassIcon className="h-6 w-6 shrink-0 fill-gray-400" />
          </div>
        </div>
      </div>

      {searchResults.length > 0 ?
        <ul role="list" className="divide-y divide-gray-300">
          {searchResults.map((cover) => (
            <CoverSearchItem
              key={cover.id}
              cover={cover}
              openCoverId={openCoverId}
              setOpenCoverId={setOpenCoverId}
              closeModal={closeModal}
              user={user}
              checkSubscription={checkSubscription}
              setInvalidLengthSubscriptionModalOpen={
                setInvalidLengthSubscriptionModalOpen
              }
              audioLengthMinLimitMinutes={audioLengthMinLimitMinutes}
              audioLengthMaxLimitMinutes={audioLengthMaxLimitMinutes}
              setInvalidLengthMaxModalOpen={setInvalidLengthMaxModalOpen}
              showCover={showCover}
              setInputCover={setInputCover}
            />
          ))}
        </ul>
      : <div className="flex h-[583px] flex-col items-center">
          <QuestionMarkCircleIcon className="mt-8 h-8 w-8 text-gray-500" />
          <SoftText elementType="p" className="mt-2">
            Search for a song
          </SoftText>
        </div>
      }
    </Modal>
  );
}
